import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Tag, Space, Popconfirm, message, Modal, Form, Input, Select, Row, Col, Empty,
  InputNumber, Switch, Divider, Drawer, Radio, Checkbox, DatePicker, Upload,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CopyOutlined,
  ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// 表单字段类型
interface FormField {
  id: string;
  name: string;
  label: string;
  type: 'input' | 'textarea' | 'number' | 'select' | 'date' | 'dateRange' | 'radio' | 'checkbox' | 'switch' | 'file';
  required: boolean;
  placeholder?: string;
  defaultValue?: any;
  options?: { label: string; value: string }[];
  span?: number;
  visible?: boolean;
  disabled?: boolean;
  rules?: { min?: number; max?: number; pattern?: string; message?: string }[];
}

// 表单模板
interface FormTemplate {
  id: string;
  name: string;
  key: string;
  description: string;
  category: string;
  fields: FormField[];
  status: number; // 0-草稿 1-已发布
  createdAt: string;
  updatedAt: string;
}

const FormDesigner: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [designerVisible, setDesignerVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  
  const [templateForm] = Form.useForm();
  const [fieldForm] = Form.useForm();
  const [previewForm] = Form.useForm();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    // 模拟从本地存储加载
    try {
      const saved = localStorage.getItem('formTemplates');
      if (saved) {
        setTemplates(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveTemplates = (newTemplates: FormTemplate[]) => {
    localStorage.setItem('formTemplates', JSON.stringify(newTemplates));
    setTemplates(newTemplates);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    templateForm.resetFields();
    setFormFields([]);
    setDesignerVisible(true);
  };

  const handleEdit = (template: FormTemplate) => {
    setEditingTemplate(template);
    templateForm.setFieldsValue({
      name: template.name,
      key: template.key,
      description: template.description,
      category: template.category,
    });
    setFormFields(template.fields || []);
    setDesignerVisible(true);
  };

  const handleCopy = (template: FormTemplate) => {
    const newTemplate: FormTemplate = {
      ...template,
      id: `template_${Date.now()}`,
      name: `${template.name}_副本`,
      key: `${template.key}_copy`,
      status: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    saveTemplates([...templates, newTemplate]);
    message.success('复制成功');
  };

  const handleDelete = (id: string) => {
    saveTemplates(templates.filter(t => t.id !== id));
    message.success('删除成功');
  };

  const handlePublish = (id: string) => {
    saveTemplates(templates.map(t => t.id === id ? { ...t, status: 1, updatedAt: new Date().toISOString() } : t));
    message.success('发布成功');
  };

  const handleDisable = (id: string) => {
    saveTemplates(templates.map(t => t.id === id ? { ...t, status: 0, updatedAt: new Date().toISOString() } : t));
    message.success('停用成功');
  };

  const saveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      const now = new Date().toISOString();
      
      if (editingTemplate) {
        // 更新
        saveTemplates(templates.map(t => t.id === editingTemplate.id ? {
          ...t,
          ...values,
          fields: formFields,
          updatedAt: now,
        } : t));
        message.success('保存成功');
      } else {
        // 新建
        const newTemplate: FormTemplate = {
          id: `template_${Date.now()}`,
          ...values,
          fields: formFields,
          status: 0,
          createdAt: now,
          updatedAt: now,
        };
        saveTemplates([...templates, newTemplate]);
        message.success('创建成功');
      }
      setDesignerVisible(false);
    } catch {}
  };

  const handlePreview = (template: FormTemplate) => {
    setEditingTemplate(template);
    setFormFields(template.fields || []);
    previewForm.resetFields();
    setPreviewVisible(true);
  };

  // 字段操作
  const addField = (type: FormField['type']) => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      name: `field_${formFields.length + 1}`,
      label: getFieldTypeLabel(type) + (formFields.length + 1),
      type,
      required: false,
      span: 12,
      visible: true,
      disabled: false,
      options: ['select', 'radio', 'checkbox'].includes(type) ? [
        { label: '选项1', value: 'option1' },
        { label: '选项2', value: 'option2' },
      ] : undefined,
    };
    setFormFields([...formFields, newField]);
  };

  const editField = (field: FormField) => {
    setEditingField(field);
    fieldForm.setFieldsValue({
      ...field,
      options: field.options ? field.options.map(o => `${o.label}:${o.value}`).join('\n') : '',
    });
    setFieldModalVisible(true);
  };

  const saveField = async () => {
    try {
      const values = await fieldForm.validateFields();
      let options: { label: string; value: string }[] | undefined;
      if (values.options && ['select', 'radio', 'checkbox'].includes(values.type)) {
        options = values.options.split('\n').filter((line: string) => line.trim()).map((line: string) => {
          const [label, value] = line.split(':');
          return { label: label?.trim() || '', value: value?.trim() || label?.trim() || '' };
        });
      }
      const updatedField: FormField = { ...editingField!, ...values, options };
      setFormFields(fields => fields.map(f => f.id === editingField?.id ? updatedField : f));
      setFieldModalVisible(false);
      message.success('保存成功');
    } catch {}
  };

  const removeField = (fieldId: string) => {
    setFormFields(fields => fields.filter(f => f.id !== fieldId));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...formFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFormFields(newFields);
  };

  const getFieldTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      input: '单行文本',
      textarea: '多行文本',
      number: '数字',
      select: '下拉选择',
      date: '日期',
      dateRange: '日期范围',
      radio: '单选',
      checkbox: '多选',
      switch: '开关',
      file: '文件上传',
    };
    return map[type] || type;
  };

  // 渲染表单字段
  const renderFormField = (field: FormField) => {
    const commonProps = { placeholder: field.placeholder, disabled: field.disabled };
    switch (field.type) {
      case 'input': return <Input {...commonProps} />;
      case 'textarea': return <TextArea {...commonProps} rows={3} />;
      case 'number': return <InputNumber {...commonProps} style={{ width: '100%' }} />;
      case 'select': return <Select {...commonProps}>{field.options?.map(opt => <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>)}</Select>;
      case 'date': return <DatePicker placeholder={field.placeholder} disabled={field.disabled} style={{ width: '100%' }} />;
      case 'dateRange': return <RangePicker disabled={field.disabled} style={{ width: '100%' }} />;
      case 'radio': return <Radio.Group disabled={field.disabled}>{field.options?.map(opt => <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>)}</Radio.Group>;
      case 'checkbox': return <Checkbox.Group disabled={field.disabled} options={field.options?.map(opt => ({ label: opt.label, value: opt.value }))} />;
      case 'switch': return <Switch disabled={field.disabled} />;
      case 'file': return <Upload disabled={field.disabled}><Button icon={<UploadOutlined />}>上传文件</Button></Upload>;
      default: return <Input {...commonProps} />;
    }
  };

  // 实时预览面板
  const renderLivePreview = () => {
    if (formFields.length === 0) {
      return <Empty description="添加字段后可实时预览表单效果" style={{ padding: '40px 0' }} />;
    }
    return (
      <div style={{ background: '#fafafa', padding: 16, borderRadius: 8, minHeight: 300 }}>
        <div style={{ background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{templateForm.getFieldValue('name') || '表单标题'}</h3>
            {templateForm.getFieldValue('description') && (
              <p style={{ margin: '8px 0 0', color: '#666', fontSize: 13 }}>{templateForm.getFieldValue('description')}</p>
            )}
          </div>
          <Form layout="vertical" size="middle">
            <Row gutter={16}>
              {formFields.filter(f => f.visible !== false).map(field => (
                <Col span={field.span || 12} key={field.id}>
                  <Form.Item
                    label={<span>{field.label}{field.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}</span>}
                    style={{ marginBottom: 16 }}
                  >
                    {renderFormField(field)}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
            <Space>
              <Button>取消</Button>
              <Button type="primary">提交</Button>
            </Space>
          </div>
        </div>
      </div>
    );
  };

  const fieldTypeOptions = [
    { label: '单行文本', value: 'input', icon: '📝' },
    { label: '多行文本', value: 'textarea', icon: '📄' },
    { label: '数字', value: 'number', icon: '🔢' },
    { label: '下拉选择', value: 'select', icon: '📋' },
    { label: '日期', value: 'date', icon: '📅' },
    { label: '日期范围', value: 'dateRange', icon: '📆' },
    { label: '单选', value: 'radio', icon: '⭕' },
    { label: '多选', value: 'checkbox', icon: '☑️' },
    { label: '开关', value: 'switch', icon: '🔘' },
    { label: '文件上传', value: 'file', icon: '📎' },
  ];

  const columns: ColumnsType<FormTemplate> = [
    { title: '表单名称', dataIndex: 'name', key: 'name' },
    { title: '表单标识', dataIndex: 'key', key: 'key', render: (key: string) => <Tag>{key}</Tag> },
    { title: '字段数', key: 'fieldCount', render: (_, record) => record.fields?.length || 0 },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (status: number) => <Tag color={status === 1 ? 'success' : 'default'}>{status === 1 ? '已发布' : '草稿'}</Tag>,
    },
    {
      title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '操作', key: 'action', width: 300,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>预览</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record)}>复制</Button>
          {record.status === 0 ? (
            <Popconfirm title="确定发布？" onConfirm={() => handlePublish(record.id)}>
              <Button size="small" type="primary">发布</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title="确定停用？" onConfirm={() => handleDisable(record.id)}>
              <Button size="small" danger>停用</Button>
            </Popconfirm>
          )}
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="表单设计" extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建表单</Button>}>
        <Table columns={columns} dataSource={templates} rowKey="id" loading={loading} pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }} />
      </Card>

      {/* 表单设计器抽屉 */}
      <Drawer
        title={editingTemplate ? '编辑表单' : '新建表单'}
        open={designerVisible}
        onClose={() => setDesignerVisible(false)}
        size="95%"
        extra={<Button type="primary" icon={<SaveOutlined />} onClick={saveTemplate}>保存</Button>}
      >
        <Form form={templateForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="name" label="表单名称" rules={[{ required: true, message: '请输入表单名称' }]}>
                <Input placeholder="如: 请假申请表" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="key" label="表单标识" rules={[{ required: true, message: '请输入表单标识' }]}>
                <Input placeholder="如: leave_form" disabled={!!editingTemplate} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="description" label="描述">
                <Input placeholder="表单描述" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider style={{ margin: '12px 0' }}>字段设计与预览</Divider>
        
        <Row gutter={16} style={{ height: 'calc(100vh - 280px)' }}>
          {/* 左侧：字段组件 */}
          <Col span={4}>
            <Card title="字段组件" size="small" style={{ height: '100%', overflow: 'auto' }} styles={{ body: { padding: 12 } }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fieldTypeOptions.map(opt => (
                  <Button
                    key={opt.value}
                    size="small"
                    style={{ textAlign: 'left' }}
                    onClick={() => addField(opt.value as FormField['type'])}
                  >
                    {opt.icon} {opt.label}
                  </Button>
                ))}
              </div>
            </Card>
          </Col>
          
          {/* 中间：字段列表 */}
          <Col span={10}>
            <Card title={`字段列表 (${formFields.length})`} size="small" style={{ height: '100%', overflow: 'auto' }}>
              {formFields.length === 0 ? (
                <Empty description="点击左侧组件添加字段" style={{ padding: '40px 0' }} />
              ) : (
                <Table
                  dataSource={formFields}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 'calc(100vh - 400px)' }}
                  columns={[
                    { title: '字段名', dataIndex: 'name', key: 'name', width: 100, ellipsis: true },
                    { title: '标签', dataIndex: 'label', key: 'label', width: 100, ellipsis: true },
                    { title: '类型', dataIndex: 'type', key: 'type', width: 80, render: (type: string) => getFieldTypeLabel(type) },
                    { title: '必填', dataIndex: 'required', key: 'required', width: 50, render: (r: boolean) => r ? <Tag color="red">是</Tag> : <Tag>否</Tag> },
                    {
                      title: '操作', key: 'action', width: 120,
                      render: (_, record, index) => (
                        <Space size={0}>
                          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => editField(record)} />
                          <Button type="link" size="small" icon={<ArrowUpOutlined />} onClick={() => moveField(index, 'up')} disabled={index === 0} />
                          <Button type="link" size="small" icon={<ArrowDownOutlined />} onClick={() => moveField(index, 'down')} disabled={index === formFields.length - 1} />
                          <Popconfirm title="确定删除？" onConfirm={() => removeField(record.id)}>
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </Col>

          {/* 右侧：实时预览 */}
          <Col span={10}>
            <Card 
              title={<span><EyeOutlined style={{ marginRight: 8 }} />实时预览</span>} 
              size="small" 
              style={{ height: '100%', overflow: 'auto' }}
              styles={{ body: { padding: 12 } }}
            >
              {renderLivePreview()}
            </Card>
          </Col>
        </Row>
      </Drawer>

      {/* 字段编辑弹窗 */}
      <Modal title="编辑字段" open={fieldModalVisible} onCancel={() => setFieldModalVisible(false)} onOk={saveField} width={600}>
        <Form form={fieldForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="字段名" rules={[{ required: true, message: '请输入字段名' }]}>
                <Input placeholder="如: reason" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="label" label="标签" rules={[{ required: true, message: '请输入标签' }]}>
                <Input placeholder="如: 请假原因" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="类型" rules={[{ required: true }]}>
                <Select placeholder="选择字段类型">
                  {fieldTypeOptions.map(opt => <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="span" label="栅格占比">
                <Select placeholder="选择栅格占比">
                  <Select.Option value={6}>1/4 (6)</Select.Option>
                  <Select.Option value={8}>1/3 (8)</Select.Option>
                  <Select.Option value={12}>1/2 (12)</Select.Option>
                  <Select.Option value={16}>2/3 (16)</Select.Option>
                  <Select.Option value={24}>整行 (24)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="required" label="是否必填" valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={12}><Form.Item name="disabled" label="是否禁用" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Form.Item name="placeholder" label="占位提示"><Input placeholder="如: 请输入请假原因" /></Form.Item>
          <Form.Item name="defaultValue" label="默认值"><Input placeholder="默认值" /></Form.Item>
          <Form.Item name="options" label="选项配置" extra="每行一个选项，格式: 标签:值 (如: 事假:personal)">
            <TextArea rows={4} placeholder="事假:personal&#10;病假:sick&#10;年假:annual" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 表单预览弹窗 */}
      <Modal
        title={`表单预览 - ${editingTemplate?.name}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={previewForm} layout="vertical">
          <Row gutter={16}>
            {formFields.filter(f => f.visible !== false).map(field => (
              <Col span={field.span || 12} key={field.id}>
                <Form.Item
                  name={field.name}
                  label={field.label}
                  rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined}
                  initialValue={field.defaultValue}
                >
                  {renderFormField(field)}
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
        {formFields.length === 0 && <Empty description="该表单暂无字段" />}
      </Modal>
    </div>
  );
};

export default FormDesigner;
